import { EventType } from "@/analytics/analytics-events";
import { ViewWizardStepProperties } from "@/analytics/wizard-events-properties";
import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import {
  ExclusiveModeCallbackContext,
  ModeNames,
  WizardModeProps,
  WizardStep,
} from "@/modes/mode";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  selectActiveWizardStepIndex,
  selectFurthestWizardStepIndex,
  selectWizardStepKeys,
} from "@/store/wizard/wizard-selectors";
import {
  goToNextWizardStep,
  goToPreviousWizardStep,
  goToWizardStep,
  resetWizard,
  startWizard,
} from "@/store/wizard/wizard-slice";
import { useDialog } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect, useMemo, useState } from "react";

export type WizardNavigation = {
  /** The step the user is on */
  activeStep: WizardStep;

  /** Index of the step the user is on */
  activeIndex: number;

  /** Index of the furthest step the user has reached */
  furthestIndex: number;

  /** All the steps of the wizard */
  steps: WizardStep[];

  /** True if the user can move forward, otherwise the reason why not, or false without a reason */
  canContinue: boolean | string;

  /** True while a step transition or the completion is running */
  isBusy: boolean;

  /** True if the active step is the last one */
  isLastStep: boolean;

  /** Move forward, or complete the wizard on the last step */
  next(): void;

  /** Move to the previous step */
  back(): void;

  /** Revisit an already reached step */
  goTo(stepKey: string): void;

  /** Leave the wizard without completing it, asking for confirmation if needed */
  close(): void;
};

/**
 * Keeps the wizard store in sync with the mode definition and exposes the navigation actions
 *
 * @param modeName Name of the wizard mode, used for analytics
 * @param wizard The wizard definition
 * @returns the navigation state and actions
 */
export function useWizardNavigation(
  modeName: ModeNames,
  wizard: WizardModeProps,
): WizardNavigation {
  const { steps } = wizard;
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const projectApi = useCurrentProjectApiClient();
  const { createDialog } = useDialog();
  const { handleErrorWithDialog } = useErrorHandlers();

  const stepKeys = useMemo(() => steps.map((step) => step.key), [steps]);
  const storedKeys = useAppSelector(selectWizardStepKeys);
  useEffect(() => {
    if (!isEqual(storedKeys, stepKeys)) dispatch(startWizard(stepKeys));
  }, [dispatch, stepKeys, storedKeys]);
  useEffect(
    () => () => {
      dispatch(resetWizard());
    },
    [dispatch],
  );

  const activeIndex = Math.min(
    useAppSelector(selectActiveWizardStepIndex),
    steps.length - 1,
  );
  const furthestIndex = useAppSelector(selectFurthestWizardStepIndex);
  const activeStep = steps[activeIndex];
  const isLastStep = activeIndex === steps.length - 1;

  const canContinue = useAppSelector(
    (state) => activeStep.selectCanContinue?.(state) ?? true,
  );

  useEffect(() => {
    Analytics.track<ViewWizardStepProperties>(EventType.viewWizardStep, {
      wizard: modeName,
      step: activeStep.key,
      index: activeIndex,
    });
  }, [activeIndex, activeStep.key, modeName]);

  const [isBusy, setIsBusy] = useState(false);

  const run = useCallback(
    async (
      action: (context: ExclusiveModeCallbackContext) => Promise<void>,
    ) => {
      setIsBusy(true);
      try {
        await action({ store, dispatch, projectApi, createDialog });
      } catch (error) {
        handleErrorWithDialog({ title: wizard.title, error });
      } finally {
        setIsBusy(false);
      }
    },
    [
      createDialog,
      dispatch,
      handleErrorWithDialog,
      projectApi,
      store,
      wizard.title,
    ],
  );

  const next = useCallback(() => {
    if (canContinue !== true || isBusy) return;
    void run(async (context) => {
      if (activeStep.onPrimaryAction) {
        await activeStep.onPrimaryAction(context);
        return;
      }
      if (isLastStep) {
        await wizard.onComplete(context);
        return;
      }
      await activeStep.onBeforeNext?.(context);
      dispatch(goToNextWizardStep());
    });
  }, [activeStep, canContinue, dispatch, isBusy, isLastStep, run, wizard]);

  const back = useCallback(() => {
    if (!isBusy && !activeStep.preventBack) dispatch(goToPreviousWizardStep());
  }, [activeStep.preventBack, dispatch, isBusy]);

  const goTo = useCallback(
    (stepKey: string) => {
      if (!isBusy && !activeStep.preventBack) dispatch(goToWizardStep(stepKey));
    },
    [activeStep.preventBack, dispatch, isBusy],
  );

  const close = useCallback(() => {
    if (isBusy) return;
    void run(async (context) => {
      const needsConfirmation =
        wizard.selectHasProgress?.(store.getState()) ?? false;
      if (
        needsConfirmation &&
        !(await createDialog({
          title: `Leave ${wizard.title}?`,
          content: "Your selections in this workflow will be lost.",
          confirmText: "Leave",
          cancelText: "Stay",
        }))
      ) {
        return;
      }
      await wizard.onExit(context, activeStep.key);
    });
  }, [activeStep.key, createDialog, isBusy, run, store, wizard]);

  return {
    activeStep,
    activeIndex,
    furthestIndex,
    steps,
    canContinue,
    isBusy,
    isLastStep,
    next,
    back,
    goTo,
    close,
  };
}
